import React, { useState, useEffect } from 'react';
import api from '../services/api';
import Modal from '../components/UI/Modal';

const GstRates = () => {
  const [rates, setRates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', rate: '', cgst: '', sgst: '', igst: '', hsn_sac_code: '', description: '', status: 'active' });

  const fetch = async () => {
    try {
      setLoading(true);
      const res = await api.get('/gst/list.php');
      if (res.data.success) setRates(res.data.data || []);
      else setError(res.data.message);
    } catch (e) { setError('Failed to load GST rates'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetch(); }, []);

  const openCreate = () => {
    setEditing(null);
    setForm({ name: '', rate: '', cgst: '', sgst: '', igst: '', hsn_sac_code: '', description: '', status: 'active' });
    setShowForm(true);
  };

  const openEdit = (r) => {
    setEditing(r);
    setForm({ name: r.name || '', rate: r.rate || '', cgst: r.cgst || '', sgst: r.sgst || '', igst: r.igst || '', hsn_sac_code: r.hsn_sac_code || '', description: r.description || '', status: r.status || 'active' });
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, rate: parseFloat(form.rate), cgst: parseFloat(form.cgst), sgst: parseFloat(form.sgst), igst: parseFloat(form.igst) };
      if (editing) { await api.post('/gst/update.php', { id: editing.id, ...payload }); }
      else { await api.post('/gst/create.php', payload); }
      setShowForm(false);
      fetch();
    } catch (e) { setError('Failed to save GST rate'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this GST rate? Categories using it will be unlinked.')) return;
    try { await api.post('/gst/delete.php', { id }); fetch(); }
    catch (e) { setError('Failed to delete GST rate'); }
  };

  const autoFill = (rate) => {
    const v = parseFloat(rate) || 0;
    setForm(prev => ({ ...prev, rate: v, name: `GST ${v}%`, cgst: (v / 2), sgst: (v / 2), igst: v }));
  };

  if (loading) return <div className="text-center py-20 text-gray-600 dark:text-gray-400">Loading...</div>;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-800 dark:text-white">GST Rates</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">Manage GST rate slabs: {rates.length} rates configured</p>
        </div>
        <button onClick={openCreate} className="px-6 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors">Add GST Rate</button>
      </div>

      {error && <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 text-red-700 dark:text-red-400 px-4 py-3 rounded-lg">{error}</div>}

      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-gray-700 border-b">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Rate</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">CGST</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">SGST</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">IGST</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">HSN/SAC</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {rates.length === 0 ? (
                <tr><td colSpan="8" className="px-6 py-12 text-center text-gray-500 dark:text-gray-400">No GST rates found.</td></tr>
              ) : rates.map(r => (
                <tr key={r.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                  <td className="px-6 py-4 text-sm font-medium text-gray-900 dark:text-white">{r.name}</td>
                  <td className="px-6 py-4 text-sm font-semibold text-gray-800 dark:text-gray-200">{r.rate}%</td>
                  <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-400">{r.cgst}%</td>
                  <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-400">{r.sgst}%</td>
                  <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-400">{r.igst}%</td>
                  <td className="px-6 py-4 text-sm font-mono text-gray-600 dark:text-gray-400">{r.hsn_sac_code || '-'}</td>
                  <td className="px-6 py-4">
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${r.status === 'active' ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400' : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-400'}`}>{r.status}</span>
                  </td>
                  <td className="px-6 py-4 text-sm space-x-2">
                    <button onClick={() => openEdit(r)} className="text-primary-600 hover:text-primary-900 dark:hover:text-primary-400">Edit</button>
                    <button onClick={() => handleDelete(r.id)} className="text-red-600 hover:text-red-900 dark:hover:text-red-400">Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Modal isOpen={showForm} onClose={() => setShowForm(false)} title={editing ? 'Edit GST Rate' : 'Add GST Rate'}>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Name</label>
              <input type="text" value={form.name} onChange={e => setForm({...form, name: e.target.value})} className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:text-white" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Rate (%) *</label>
              <input type="number" step="0.01" value={form.rate} onChange={e => { autoFill(e.target.value); }} className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:text-white" required />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">CGST (%)</label>
              <input type="number" step="0.01" value={form.cgst} onChange={e => setForm({...form, cgst: e.target.value})} className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:text-white" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">SGST (%)</label>
              <input type="number" step="0.01" value={form.sgst} onChange={e => setForm({...form, sgst: e.target.value})} className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:text-white" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">IGST (%)</label>
              <input type="number" step="0.01" value={form.igst} onChange={e => setForm({...form, igst: e.target.value})} className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:text-white" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">HSN/SAC Code</label>
              <input type="text" value={form.hsn_sac_code} onChange={e => setForm({...form, hsn_sac_code: e.target.value})} className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:text-white" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Status</label>
              <select value={form.status} onChange={e => setForm({...form, status: e.target.value})} className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:text-white">
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Description</label>
              <textarea value={form.description} onChange={e => setForm({...form, description: e.target.value})} rows="2" className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:text-white"></textarea>
            </div>
          </div>
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
            <button type="button" onClick={() => setShowForm(false)} className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700">Cancel</button>
            <button type="submit" className="px-6 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors">{editing ? 'Update' : 'Create'} GST Rate</button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default GstRates;
